import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { ServiceCard } from "../components/ServiceCard";
import {
  CheckCircle,
  Clock,
  Shield,
  Users,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  Star,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  const features = [
    {
      icon: Users,
      title: "Personalized Attention",
      description: "One-on-one service tailored to your specific needs",
    },
    {
      icon: CheckCircle,
      title: "Accurate & Professional",
      description: "Precise, timely service you can depend on",
    },
    {
      icon: Shield,
      title: "Confidential & Trustworthy",
      description: "Your information is safe and secure with us",
    },
    {
      icon: Clock,
      title: "Transparent Pricing",
      description: "No hidden fees, clear communication always",
    },
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center hero-pattern pt-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-fade-in-up">
              {/* Promo Badge */}
              <div className="inline-flex items-center gap-2 mb-8">
                <span
                  data-testid="promo-badge"
                  className="promo-badge bg-[#D4AF37] text-[#0F392B] px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider shadow-lg"
                >
                  <Star className="w-4 h-4 inline mr-1" />
                  10% Off for New Clients
                </span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F392B] mb-6 leading-tight">
                Tax Season Is Here.
                <br />
                <span className="text-[#D4AF37]">Accepting New Clients.</span>
              </h1>

              <p className="font-body text-lg text-[#4A5568] mb-8 max-w-xl leading-relaxed">
                Espady Multi-Service Co. provides reliable, professional, and
                personalized support for individuals, families, and small
                businesses. We handle the details so you can focus on what
                matters most.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/booking">
                  <Button
                    data-testid="hero-book-btn"
                    className="btn-primary bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                  >
                    Book an Appointment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    data-testid="hero-services-btn"
                    variant="outline"
                    className="border-2 border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white rounded-full px-8 py-6 text-lg transition-all duration-300"
                  >
                    View Our Services
                  </Button>
                </Link>
              </div>

              {/* Quick Contact */}
              <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-[#4A5568]">
                <a
                  href="tel:786-991-8919"
                  className="flex items-center gap-2 hover:text-[#0F392B] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#D4AF37]" />
                  <span className="font-body">786-991-8919</span>
                </a>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span className="font-body">West Palm Beach, FL</span>
                </span>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative hidden lg:block animate-fade-in animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c"
                  alt="Professional team at Espady Multi-Service Co."
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F392B]/30 to-transparent" />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl animate-fade-in animation-delay-400">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-[#0F392B]" />
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-[#0F392B]">
                      9+
                    </p>
                    <p className="font-body text-sm text-[#4A5568]">
                      Professional Services
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        data-testid="about-section"
        className="py-20 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1497215728101-856f4ea42174"
                alt="Professional office environment"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#D4AF37]/20 rounded-2xl -z-10" />
            </div>
            <div>
              <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
                ABOUT US
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F392B] mb-6">
                Multi-Service Solutions
                <br />
                Under One Roof
              </h2>
              <div className="gold-line mb-6" />
              <p className="font-body text-[#4A5568] leading-relaxed mb-6">
                At Espady Multi-Service Co., we understand that managing
                personal, financial, and business matters can be overwhelming.
                That's why we've created a one-stop solution for all your
                professional service needs.
              </p>
              <p className="font-body text-[#4A5568] leading-relaxed mb-8">
                Our dedicated team prioritizes professionalism, confidentiality,
                accuracy, and personalized attention. We handle the details so
                you can focus on what matters most to you and your family.
              </p>
              <Link to="/services">
                <Button className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-6 py-3 font-medium">
                  Explore Our Services
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        data-testid="services-section"
        className="py-20 md:py-32 bg-[#F9F9F7]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
              OUR SERVICES
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F392B] mb-4">
              Professional Services
              <br />
              Tailored to Your Needs
            </h2>
            <div className="gold-line mx-auto mb-6" />
            <p className="font-body text-[#4A5568] max-w-2xl mx-auto">
              From tax preparation to business development, we offer
              comprehensive solutions designed to simplify your life.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.slice(0, 4).map((service, index) => (
              <ServiceCard
                key={service.id}
                service={service}
                variant={index === 0 ? "large" : "default"}
              />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                data-testid="view-all-services-btn"
                variant="outline"
                className="border-2 border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white rounded-full px-8 py-3 font-medium"
              >
                View All Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section
        data-testid="why-choose-section"
        className="py-20 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
              WHY CHOOSE US
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F392B] mb-4">
              Your Success Is Our Priority
            </h2>
            <div className="gold-line mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-card-${index}`}
                className="bg-[#F9F9F7] p-8 rounded-2xl border border-transparent hover:border-[#0F392B]/10 transition-all duration-300 group"
              >
                <div className="w-14 h-14 rounded-xl bg-[#0F392B]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                  <feature.icon className="w-7 h-7 text-[#0F392B]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#0F392B] mb-3">
                  {feature.title}
                </h3>
                <p className="font-body text-[#4A5568] text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section
        data-testid="contact-cta-section"
        className="py-20 md:py-32 bg-[#0F392B] relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37] blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-[#D4AF37] blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Ready to Get Started?
              </h2>
              <p className="font-body text-white/80 text-lg mb-8 leading-relaxed">
                Contact us today to schedule your appointment. Our team is ready
                to assist you with all your professional service needs.
              </p>
              <div className="space-y-4 text-white/80">
                <div className="flex items-center gap-4">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-body">
                    1368-B S Military Trail, Suite R, West Palm Beach, FL 33415
                  </span>
                </div>
                <a
                  href="tel:786-991-8919"
                  className="flex items-center gap-4 hover:text-[#D4AF37] transition-colors"
                >
                  <Phone className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-body">786-991-8919</span>
                </a>
                <a
                  href="mailto:espadywisellc@gmail.com"
                  className="flex items-center gap-4 hover:text-[#D4AF37] transition-colors"
                >
                  <Mail className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-body">espadywisellc@gmail.com</span>
                </a>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
              <Link to="/booking">
                <Button className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 shadow-lg">
                  Book Appointment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0F392B] rounded-full px-8 py-6 text-lg transition-all duration-300"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section data-testid="map-section" className="py-20 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
              VISIT US
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F392B] mb-4">
              Our Location
            </h2>
            <div className="gold-line mx-auto" />
          </div>
          <div className="map-container rounded-2xl overflow-hidden shadow-xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3565.9!2d-80.0897!3d26.6425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d8d6c4e3a0b3b1%3A0x1234567890abcdef!2s1368%20S%20Military%20Trail%2C%20West%20Palm%20Beach%2C%20FL%2033415!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Espady Multi-Service Co. Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
